import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import tw from "twin.macro";
import Modal from "react-modal";
import GradientButton from "../../components/GradientButton";
import {
  getBrazilianStates,
  getCitiesByState,
} from "../../core/services/ibgeService";
import { getProfessionalDirectory } from "../../core/services/professionalDirectoryService";

const COLORS = {
  teal: "#437672",
  tealButton: "#4f8884",
  orange: "#A4400A",
  body: "#4D4B4B",
};

const LOGIN_URL = "https://www.trataz.com.br/login";

const FALLBACK_STATES = [
  { value: "SC", label: "Santa Catarina" },
  { value: "PR", label: "Parana" },
  { value: "RS", label: "Rio Grande do Sul" },
  { value: "SP", label: "Sao Paulo" },
];

const FALLBACK_CITIES_BY_STATE = {
  SC: [
    { value: "Joinville", label: "Joinville" },
    { value: "Florianopolis", label: "Florianopolis" },
    { value: "Blumenau", label: "Blumenau" },
    { value: "Chapeco", label: "Chapeco" },
    { value: "Itajai", label: "Itajai" },
  ],
  PR: [
    { value: "Curitiba", label: "Curitiba" },
    { value: "Londrina", label: "Londrina" },
    { value: "Maringa", label: "Maringa" },
    { value: "Cascavel", label: "Cascavel" },
  ],
  RS: [
    { value: "Porto Alegre", label: "Porto Alegre" },
    { value: "Caxias do Sul", label: "Caxias do Sul" },
    { value: "Pelotas", label: "Pelotas" },
    { value: "Santa Maria", label: "Santa Maria" },
  ],
  SP: [
    { value: "Sao Paulo", label: "Sao Paulo" },
    { value: "Campinas", label: "Campinas" },
    { value: "Santos", label: "Santos" },
    { value: "Ribeirao Preto", label: "Ribeirao Preto" },
  ],
};

const normalizeSearchValue = (value) =>
  value
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const FindProfessionalModalRoot = styled(Modal)`
  position: absolute;
  inset: 50% auto auto 50%;
  transform: translate(-50%, -50%);
  width: min(1120px, calc(100vw - 32px));
  max-height: calc(100vh - 32px);
  overflow: auto;
  background: #fff;
  border-radius: 8px;
  outline: none;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.24);
`;

const ModalInner = styled.div`
  ${tw`relative`}
  min-height: 850px;
  padding: 3rem 1.5rem 0;
  overflow: hidden;
  background: #fff;

  &::after {
    content: "";
    position: absolute;
    right: -185px;
    bottom: -470px;
    width: 650px;
    height: 805px;
    background-image: url("/novas-imagens/popup-find-professional/expiral.png");
    background-repeat: no-repeat;
    background-size: contain;
    background-position: center;
    opacity: 0.13;
    pointer-events: none;
    transform: rotate(-10deg);
    transform-origin: center;
    z-index: 0;
  }

  @media (min-width: 768px) {
    padding: 5.2rem 5.6rem 0;
  }

  @media (max-width: 767px) {
    min-height: 0;

    &::after {
      right: -280px;
      bottom: -360px;
      width: 500px;
      height: 620px;
    }
  }
`;

const ModalClose = styled.button`
  ${tw`absolute`}
  top: 1.25rem;
  right: 1.25rem;
  width: 44px;
  height: 44px;
  border: 0;
  border-radius: 9999px;
  background: rgba(0, 0, 0, 0.06);
  color: #444;
  font-size: 24px;
  cursor: pointer;
  z-index: 5;
`;

const Header = styled.div`
  ${tw`relative flex justify-between items-start gap-6`}
  z-index: 2;
`;

const Title = styled.h2`
  font-family: "Montserrat", sans-serif;
  font-size: 18px;
  line-height: 1.05;
  font-weight: 700;
  color: ${COLORS.teal};
  text-transform: uppercase;

  @media (max-width: 767px) {
    margin-top: 5rem;
  }
`;

const Logo = styled.img`
  width: 190px;
  height: 90px;
  display: block;
`;

const ContentGrid = styled.div`
  ${tw`relative grid gap-8`}
  z-index: 2;
  margin-top: 4rem;

  @media (min-width: 1024px) {
    grid-template-columns: minmax(0, 470px);
    align-items: start;
  }
`;

const FormArea = styled.div``;

const Label = styled.label`
  display: block;
  margin: 0 0 0.9rem 1rem;
  font-family: "Montserrat", sans-serif;
  font-size: 14px;
  line-height: 1.1;
  font-weight: 500;
  color: ${COLORS.teal};
  text-transform: uppercase;
`;

const FieldGroup = styled.div`
  position: relative;
  margin-bottom: 1.1rem;

  ${({ $nested }) =>
    $nested &&
    `
      margin-left: 2.4rem;

      &::before {
        content: "";
        position: absolute;
        left: -26px;
        top: -15px;
        width: 26px;
        height: 35px;
        border-left: 2px solid #00837f;
        border-bottom: 2px solid #00837f;
      }
    `}

  @media (max-width: 767px) {
    ${({ $nested }) => $nested && "margin-left: 1.2rem;"}
  }
`;

const SelectShell = styled.div`
  position: relative;
  width: ${({ $wide }) => ($wide ? "470px" : "340px")};
  max-width: 100%;
`;

const SelectTrigger = styled.button`
  width: 100%;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0 20px 0 26px;
  border: 1px solid rgba(67, 118, 114, 0.12);
  border-radius: 9999px;
  background: #dedede;
  color: ${COLORS.teal};
  box-shadow: 0 10px 18px rgba(0, 0, 0, 0.08);
  font-family: "Montserrat", sans-serif;
  font-size: 14px;
  font-weight: 500;
  text-align: left;
  outline: none;
  cursor: pointer;

  &:focus-visible {
    box-shadow: 0 0 0 3px rgba(67, 118, 114, 0.2);
  }
`;

const SearchSelectTrigger = styled.div`
  width: 100%;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0 20px 0 26px;
  border: 1px solid rgba(67, 118, 114, 0.12);
  border-radius: 9999px;
  background: #dedede;
  color: ${COLORS.teal};
  box-shadow: 0 10px 18px rgba(0, 0, 0, 0.08);
  cursor: text;

  &:focus-within {
    box-shadow: 0 0 0 3px rgba(67, 118, 114, 0.2);
  }
`;

const SelectInput = styled.input`
  flex: 1;
  min-width: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: ${COLORS.teal};
  font-family: "Montserrat", sans-serif;
  font-size: 14px;
  font-weight: 500;

  &::placeholder {
    color: ${COLORS.teal};
    opacity: 0.78;
  }
`;

const SelectChevron = styled.span`
  width: 0;
  height: 0;
  flex: 0 0 auto;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 8px solid #00837f;
`;

const SelectMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  z-index: 99999;
  max-height: 220px;
  overflow: auto;
  padding: 0.45rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  background: #171717;
  box-shadow: 0 20px 45px rgba(0, 0, 0, 0.2);
`;

const SelectOption = styled.button`
  width: 100%;
  height: 42px;
  padding: 0 14px;
  border: 0;
  border-radius: 12px;
  background: ${({ $selected }) => ($selected ? "#444" : "transparent")};
  color: #f5f5f5;
  font-family: "Montserrat", sans-serif;
  font-size: 14px;
  font-weight: 400;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: #3f3f3f;
  }
`;

const SelectEmpty = styled.div`
  min-height: 42px;
  display: flex;
  align-items: center;
  padding: 0 14px;
  color: #f5f5f5;
  font-family: "Montserrat", sans-serif;
  font-size: 14px;
  font-weight: 400;
  text-transform: lowercase;
`;

const SelectPlaceholder = styled.span`
  opacity: 0.78;
`;

const Results = styled.div`
  width: 440px;
  max-width: 100%;
  margin-top: 3rem;
`;

const ResultsTitle = styled.div`
  margin-bottom: 0.8rem;
  font-family: "Montserrat", sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: ${COLORS.teal};
  text-transform: uppercase;
`;

const ResultsList = styled.div`
  height: 210px;
  overflow: auto;
  padding-right: 0.6rem;
`;

const ResultLink = styled(Link)`
  display: block;
  padding: 0.72rem 0.2rem 0.78rem;
  border-bottom: 1px solid #cccccc;
  font-family: "Montserrat", sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: ${COLORS.teal};
  text-decoration: none;
  text-transform: uppercase;
`;

const ResultsMessage = styled.div`
  padding: 0.72rem 0.2rem;
  font-family: "Montserrat", sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: ${COLORS.teal};
  text-transform: uppercase;
`;

const PatientCta = styled.div`
  max-width: 460px;
  align-self: center;
  font-family: "Montserrat", sans-serif;
  font-size: 14px;
  line-height: 1.15;
  font-weight: 700;
  color: ${COLORS.teal};
  text-transform: uppercase;

  @media (max-width: 767px) {
    text-align: center;
  }
`;

const PatientButton = styled(GradientButton).attrs({
  $variant: "teal",
})`
  margin-top: 1.1rem;
  width: 200px;
  height: 48px;
  font-family: "Montserrat", sans-serif;
  font-size: 14px;
  font-weight: 500;
  text-transform: uppercase;
`;

const TratiImage = styled.img`
  width: 250px;
  max-width: 100%;
  height: auto;
  display: block;

  @media (max-width: 767px) {
    width: min(58vw, 230px);
    margin: 0 auto;
  }
`;

const BottomArea = styled.div`
  ${tw`relative grid gap-6 items-end`}
  z-index: 2;
  margin-top: 1.25rem;

  @media (min-width: 768px) {
    grid-template-columns: 270px minmax(0, 460px);
    column-gap: 3rem;
    align-items: end;
  }
`;

function CustomSelect({
  id,
  label,
  value,
  options,
  onChange,
  wide,
  searchable,
  searchValue,
  onSearchChange,
  placeholder,
  onClear,
}) {
  const [open, setOpen] = React.useState(false);
  const selected = options.find((option) => option.value === value);

  if (searchable) {
    return (
      <SelectShell $wide={wide}>
        <SearchSelectTrigger>
          <SelectInput
            id={id}
            aria-label={label}
            value={searchValue}
            placeholder={placeholder}
            onFocus={() => setOpen(true)}
            onChange={(event) => {
              onSearchChange(event.target.value);
              setOpen(true);
            }}
            onKeyDown={(event) => {
              if (event.key === "Backspace") {
                event.preventDefault();
                onSearchChange("");
                setOpen(true);
              }
            }}
            onBlur={() => window.setTimeout(() => setOpen(false), 120)}
          />
          <SelectChevron aria-hidden="true" />
        </SearchSelectTrigger>

        {open && (
          <SelectMenu role="listbox" aria-labelledby={id}>
            {options.length > 0 ? (
              options.map((option) => (
                <SelectOption
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={option.value === value}
                  $selected={option.value === value}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    onChange(option.value, option.label);
                    setOpen(false);
                  }}
                >
                  {option.label}
                </SelectOption>
              ))
            ) : (
              <SelectEmpty>nenhum registro encontrado</SelectEmpty>
            )}
          </SelectMenu>
        )}
      </SelectShell>
    );
  }

  return (
    <SelectShell $wide={wide}>
      <SelectTrigger
        id={id}
        type="button"
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === "Backspace" && onClear) {
            event.preventDefault();
            onClear();
            setOpen(false);
          }
        }}
        onBlur={() => window.setTimeout(() => setOpen(false), 120)}
      >
        {selected ? (
          <span>{selected.label}</span>
        ) : (
          <SelectPlaceholder>{placeholder}</SelectPlaceholder>
        )}
        <SelectChevron aria-hidden="true" />
      </SelectTrigger>

      {open && (
        <SelectMenu role="listbox" aria-labelledby={id}>
          {options.length > 0 ? (
            options.map((option) => (
              <SelectOption
                key={option.value}
                type="button"
                role="option"
                aria-selected={option.value === value}
                $selected={option.value === value}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
              >
                {option.label}
              </SelectOption>
            ))
          ) : (
            <SelectEmpty>nenhum registro encontrado</SelectEmpty>
          )}
        </SelectMenu>
      )}
    </SelectShell>
  );
}

export default function FindProfessionalModal({ isOpen, onRequestClose }) {
  const [stateOptions, setStateOptions] = React.useState(FALLBACK_STATES);
  const [cityOptions, setCityOptions] = React.useState([]);
  const [state, setState] = React.useState("");
  const [city, setCity] = React.useState("");
  const [citySearch, setCitySearch] = React.useState("");
  const [limit, setLimit] = React.useState(20);
  const [professionals, setProfessionals] = React.useState([]);
  const [hasMoreProfessionals, setHasMoreProfessionals] = React.useState(false);
  const [professionalsLoading, setProfessionalsLoading] = React.useState(false);

  const filteredCityOptions = React.useMemo(() => {
    const search = normalizeSearchValue(citySearch);

    if (!search) {
      return cityOptions;
    }

    return cityOptions.filter((option) =>
      normalizeSearchValue(option.label).includes(search)
    );
  }, [cityOptions, citySearch]);

  React.useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    let isMounted = true;

    getBrazilianStates()
      .then((states) => {
        if (!isMounted || states.length === 0) {
          return;
        }

        setStateOptions(states);
      })
      .catch(() => {
        if (isMounted) {
          setStateOptions(FALLBACK_STATES);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    if (!state) {
      setCityOptions([]);
      setCity("");
      setCitySearch("");
      return undefined;
    }

    let isMounted = true;

    getCitiesByState(state)
      .then((cities) => {
        if (!isMounted || cities.length === 0) {
          return;
        }

        setCityOptions(cities);
      })
      .catch(() => {
        if (!isMounted) {
          return;
        }

        const fallbackCities =
          FALLBACK_CITIES_BY_STATE[state] || FALLBACK_CITIES_BY_STATE.SC;

        setCityOptions(fallbackCities);
      });

    return () => {
      isMounted = false;
    };
  }, [isOpen, state]);

  React.useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    let isMounted = true;
    const timer = window.setTimeout(() => {
      setProfessionalsLoading(true);

      getProfessionalDirectory({ state, city: citySearch || city, limit })
        .then((directory) => {
          if (!isMounted) {
            return;
          }

          setProfessionals(directory.professionals || []);
          setHasMoreProfessionals(Boolean(directory.pagination?.hasMore));
        })
        .catch(() => {
          if (!isMounted) {
            return;
          }

          setProfessionals([]);
          setHasMoreProfessionals(false);
        })
        .finally(() => {
          if (isMounted) {
            setProfessionalsLoading(false);
          }
        });
    }, 300);

    return () => {
      isMounted = false;
      window.clearTimeout(timer);
    };
  }, [city, citySearch, isOpen, limit, state]);

  const handleStateChange = (nextState) => {
    setState(nextState);
    const fallbackCities =
      FALLBACK_CITIES_BY_STATE[nextState] || FALLBACK_CITIES_BY_STATE.SC;

    setCityOptions(fallbackCities);
    setCity("");
    setCitySearch("");
    setLimit(20);
  };

  const handleStateClear = () => {
    setState("");
    setCityOptions([]);
    setCity("");
    setCitySearch("");
    setLimit(20);
  };

  const handleCitySearchChange = (nextCitySearch) => {
    setCitySearch(nextCitySearch);
    setCity(nextCitySearch);
    setLimit(20);
  };

  const handleCityChange = (nextCity, nextCityLabel) => {
    setCity(nextCity);
    setCitySearch(nextCityLabel || nextCity);
    setLimit(20);
  };

  const handleResultsScroll = (event) => {
    const element = event.currentTarget;
    const nearBottom =
      element.scrollTop + element.clientHeight >= element.scrollHeight - 24;

    if (nearBottom && hasMoreProfessionals && !professionalsLoading) {
      setLimit((current) => Math.min(current + 20, 100));
    }
  };

  return (
    <FindProfessionalModalRoot
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      shouldCloseOnOverlayClick
      overlayClassName="plans-modal-overlay"
    >
      <ModalInner>
        <ModalClose onClick={onRequestClose} aria-label="Fechar modal">
          x
        </ModalClose>

        <Header>
          <Title>Encontre um profissional</Title>
          <Logo src="/novas-imagens/popup-planos/tratazLogo.png" alt="Trataz" />
        </Header>

        <ContentGrid>
          <FormArea>
            <Label htmlFor="professional-state">Escolha o estado</Label>
            <FieldGroup>
              <CustomSelect
                id="professional-state"
                label="Escolha o estado"
                value={state}
                options={stateOptions}
                onChange={handleStateChange}
                placeholder="Selecione o estado"
                onClear={handleStateClear}
              />
            </FieldGroup>

            <FieldGroup $nested>
              <CustomSelect
                id="professional-city"
                label="Escolha o municipio"
                value={city}
                options={filteredCityOptions}
                onChange={handleCityChange}
                searchable
                searchValue={citySearch}
                onSearchChange={handleCitySearchChange}
                placeholder="Ex.: Joinville"
                wide
              />
            </FieldGroup>
          </FormArea>

          <Results>
            <ResultsTitle>Resultado:</ResultsTitle>
            <ResultsList onScroll={handleResultsScroll}>
              {professionals.map((professional) => (
                <ResultLink
                  key={professional.id}
                  to={LOGIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {professional.fullName}
                  {professional.city ? ` - ${professional.city}` : ""}
                  {professional.state ? ` - ${professional.state}` : ""}
                </ResultLink>
              ))}

              {professionalsLoading && (
                <ResultsMessage>Carregando profissionais...</ResultsMessage>
              )}

              {!professionalsLoading && professionals.length === 0 && (
                <ResultsMessage>nenhum registro encontrado</ResultsMessage>
              )}
            </ResultsList>
          </Results>
        </ContentGrid>

        <BottomArea>
          <TratiImage src="/novas-imagens/section-six/trati.png" alt="Trati" />
          <PatientCta>
            Paciente,
            <br />
            faça seu login para acessar seu
            <br />
            tratamento.
            <br />
            <PatientButton
              as={Link}
              to={LOGIN_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Clique aqui
            </PatientButton>
          </PatientCta>
        </BottomArea>
      </ModalInner>
    </FindProfessionalModalRoot>
  );
}
